import { writable, derived } from 'svelte/store';
import type { ReleaseRequest, ReleaseMetadata, AssetData, ReleaseData } from '../types/release';
import type { ValidationData } from '../types/api';

// Step type definition
export type Step = 
    | 'input' 
    | 'generating-xml' 
    | 'preview'
    | 'delivering' 
    | 'complete' 
    | 'error';

// Core release data
export const releaseConfigState = writable<ReleaseRequest>({
    outputFormat: 'ddex-ern',
    releaseProfile: 'Audio',
    releaseType: 'Single',
    ernVersion: '432',
    avsVersion: 9,
    generateAiAssets: true
});

// Current workflow snapshot
export interface ReleaseRuntimeState {
    generationStatus: 'idle' | 'running' | 'succeeded';
    generatedXML: string | null;
    releaseData: ReleaseData | null;
    releaseMetadata: ReleaseMetadata | null;
    validation: ValidationData | null;
    validationMessage: string | null;
    deliveryPath: string | null;
    deliveryStatus: 'idle' | 'pending' | 'complete';
    previewStep: 'none' | 'validation' | 'assets';
    deliveryInitiated: boolean;
    assetTriggerVersion: number;
    assetGeneration: AssetGenerationState;
    error: string | null;
}

const initialRuntimeState: ReleaseRuntimeState = {
    generationStatus: 'idle',
    generatedXML: null,
    releaseData: null,
    releaseMetadata: null,
    validation: null,
    validationMessage: null,
    deliveryPath: null,
    deliveryStatus: 'idle',
    previewStep: 'none',
    deliveryInitiated: false,
    assetTriggerVersion: 0,
    assetGeneration: {
        isGenerating: false,
        progress: null,
        error: null,
        results: null,
        requested: false
    },
    error: null
};

export const releaseRuntimeState = writable<ReleaseRuntimeState>({ ...initialRuntimeState });

// Asset generation state interface (used by components and tests)
export interface AssetGenerationState {
    isGenerating: boolean;
    progress: string | null;
    error: string | null;
    results: {
        imageUrl: string | null;
        audioUrl: string | null;
        videoUrl: string | null;
    } | null;
    requested: boolean;
}

// Phase 3: All legacy derived stores have been removed!
// Components now access state directly from releaseConfigState and releaseRuntimeState

export const releaseStore = {
    // Reset all state to initial values
    reset: () => {
        releaseConfigState.set({
            outputFormat: 'ddex-ern',
            releaseProfile: 'Audio',
            releaseType: 'Single',
            ernVersion: '432',
            avsVersion: 9,
            generateAiAssets: true
        });
        releaseStore.resetRuntime();
    },

    resetRuntime: () => {
        releaseRuntimeState.set({ ...initialRuntimeState });
    },

    // Set error state
    setError: (error: string) => {
        releaseRuntimeState.update((state) => ({
            ...state,
            generationStatus: 'idle',
            error
        }));
    },

    // Clear error state
    clearError: () => {
        releaseRuntimeState.update((state) => ({
            ...state,
            error: null
        }));
    },

    setGenerationRunning: () => {
        releaseRuntimeState.update((state) => ({
            ...state,
            generationStatus: 'running',
            previewStep: 'none',
            deliveryStatus: 'idle',
            deliveryInitiated: false,
            deliveryPath: null,
            validation: null,
            validationMessage: null,
            releaseData: null,
            generatedXML: null,
            assetTriggerVersion: 0,
            assetGeneration: { ...initialRuntimeState.assetGeneration },
            error: null
        }));
    },

    setReleaseData: (data: ReleaseData) => {
        releaseRuntimeState.update((state) => ({
            ...state,
            generationStatus: 'succeeded',
            releaseData: data,
            generatedXML: data.xml,
            releaseMetadata: data.releaseMetadata ?? null,
            deliveryStatus: 'idle',
            previewStep: 'none',
            deliveryInitiated: false,
            error: null
        }));
    },

    setValidation: (data: ValidationData) => {
        releaseRuntimeState.update((state) => ({
            ...state,
            validation: data,
            validationMessage: data.message,
            error: null
        }));
    },

    setDeliveryPath: (filePath: string) => {
        releaseRuntimeState.update((state) => ({
            ...state,
            deliveryPath: filePath,
            deliveryStatus: 'complete',
            deliveryInitiated: false,
            error: null
        }));
    },

    setDeliveryStatus: (status: ReleaseRuntimeState['deliveryStatus']) => {
        releaseRuntimeState.update((state) => ({
            ...state,
            deliveryStatus: status
        }));
    },

    setPreviewRequested: (step: 'validation' | 'assets') => {
        releaseRuntimeState.update((state) => ({
            ...state,
            previewStep: step,
            assetTriggerVersion: step === 'assets' ? state.assetTriggerVersion + 1 : state.assetTriggerVersion
        }));
    },

    setAssetGenerationRequested: () => {
        releaseRuntimeState.update((state) => ({
            ...state,
            assetTriggerVersion: state.assetTriggerVersion + 1
        }));
    },

    setDeliveryInitiated: () => {
        releaseRuntimeState.update((state) => ({
            ...state,
            deliveryInitiated: true,
            deliveryStatus: 'pending'
        }));
    },

    updateAssetProgress: (progress: string) => {
        releaseRuntimeState.update(state => ({
            ...state,
            assetGeneration: {
                ...state.assetGeneration,
                isGenerating: true,
                progress,
                error: null
            }
        }));
    },

    setAssetResults: (results: { imageUrl: string | null; audioUrl: string | null; videoUrl: string | null }) => {
        releaseRuntimeState.update(state => ({
            ...state,
            assetGeneration: {
                ...state.assetGeneration,
                isGenerating: false,
                progress: 'Asset generation completed!',
                error: null,
                results,
                requested: true
            },
            previewStep: 'assets'
        }));
    },

    setAssetError: (error: string) => {
        releaseRuntimeState.update(state => ({
            ...state,
            assetGeneration: {
                ...state.assetGeneration,
                isGenerating: false,
                progress: null,
                error,
                results: null,
                requested: true
            },
            previewStep: 'assets'
        }));
    }
};

export const computedStep = derived(
    [releaseConfigState, releaseRuntimeState],
    ([$config, $runtime]) => {
        if ($runtime.error) return 'error';
        if ($runtime.generationStatus === 'idle') return 'input';
        if ($runtime.generationStatus === 'running') return 'generating-xml';
        if (!$runtime.generatedXML || !$runtime.validation) return 'generating-xml';
        if ($runtime.deliveryStatus === 'complete') return 'complete';
        if ($runtime.deliveryStatus === 'pending' || $runtime.deliveryInitiated) return 'delivering';
        if ($runtime.previewStep === 'assets') return 'preview';
        if ($runtime.previewStep === 'validation') return 'preview';
        return 'generating-xml';
    },
    'input'
);
